// Re:Vault auto-updater - converges every open tab onto the latest deploy without
// the user clearing data, and without yanking an active browsing session.
//
// Each build bakes a unique __RVM.buildId into this bundle; the server exposes the
// latest deployed id at /__rv/version (no-store). We poll it (after boot, on
// focus / visibility / online, on a slow interval, on bfcache restore, and
// whenever an asset fails to load) and compare to our baked id. A mismatch means
// a newer build is live.
//
// Converging requires a FULL reload onto fresh (no-cache) HTML, because the build
// randomizes every asset name including the service-worker filename - the running
// page can't register the new SW by name; only fresh HTML carries it. And because
// the SW uses the canonical waiting model (it parks in "waiting" instead of
// hijacking open tabs), we must drive the new SW to activate (SKIP_WAITING) and
// reload on `controllerchange`, so the reloaded page is controlled by a SW whose
// engine prefixes match the new bundle. Old /dist assets and wisp paths are
// retained across the last few builds (build.mjs + server) so an un-updated tab
// keeps working until it reloads.
//
// UX: auto-reload only when it's SAFE (nothing mid-use in a live proxied tab -
// the caller decides via isSafeToReload); otherwise show a small dismissible
// toast. sessionRestore (default on) rebuilds tabs after a reload, so a safe
// auto-reload is near-invisible. Both behaviors are gated by the autoUpdate /
// updateNotify settings.
import { get } from "../shared/settings-store.js";
import { emitIsland } from "./island-gate.js";

// esbuild `define` (build.mjs) replaces the member expression __RVM.buildId with a
// string literal at build time. Reference it directly - NOT a bare `__RVM`, which
// has no define and would throw ReferenceError at runtime.
const MY = __RVM.buildId;
const VERSION_URL = "/__rv/version";
// The presence beacon (presence.js) reports the live build id about once a minute while the
// tab is visible, which is the main detection path now. Polling /__rv/version is only the
// backstop (hidden tabs, a blocked beacon) and the targeted check after an asset failure.
const POLL_MS = 20 * 60 * 1000; // slow background backstop
const THROTTLE_MS = 5 * 60 * 1000; // min gap between focus/visibility-driven checks
const RELOAD_KEY = "rv:reloaded-for"; // sessionStorage one-shot, keyed by target build id

// Whether a service worker was already controlling this page at load. Captured at
// module-eval time (before proxy.boot registers the SW). Used to avoid reloading
// on a first-ever SW install (which also fires controllerchange).
const hadController = !!(navigator.serviceWorker && navigator.serviceWorker.controller);

let safeFn = () => true;
let targetId = null; // build id we're trying to converge to
let lastCheck = 0;
let latched = false; // a newer build has been detected this session
let refreshing = false; // a reload is in flight

let toastEl = null;

const autoUpdateOn = () => get("autoUpdate") !== false;
const notifyOn = () => get("updateNotify") !== false;

async function latest() {
	try {
		const r = await fetch(VERSION_URL, { cache: "no-store", credentials: "omit" });
		if (!r.ok) return null;
		const j = await r.json();
		// The engine switches ride along (rollback without a release); app/proxy.js applies them.
		if (j && j.engines && typeof j.engines === "object") { try { window.dispatchEvent(new CustomEvent("rv:engines", { detail: j.engines })); } catch (e) { void e; } }
		// Slots kill switch: a running page drops its network slots (slots/main.js), no new poller.
		if (j && j.slotsOff === true) { try { window.dispatchEvent(new Event("rv-slots-off")); } catch { /* no slots on this page */ } }
		return j && typeof j.buildId === "string" ? j : null;
	} catch (e) {
		return null;
	}
}

function alreadyTried(id) {
	try { return !!id && sessionStorage.getItem(RELOAD_KEY) === id; } catch (e) { return false; }
}

function reloadNow() {
	if (refreshing) return;
	refreshing = true;
	// Loop-guard: record what we're converging TO. If an edge briefly serves
	// stale HTML and we land back on the old build, initUpdates() won't clear this
	// key (MY !== targetId), so the next detection shows the toast instead of
	// reloading again.
	try { if (targetId) sessionStorage.setItem(RELOAD_KEY, targetId); } catch (e) {}
	window.__rvUpdating = true; // main.js: bypass the "confirm close many tabs" beforeunload guard
	location.reload();
}

// Converge onto the new build with a PLAIN reload. This is the whole mechanism:
// routing prefixes are stable across builds and the last few generations of assets
// are retained, so the currently-controlling service worker (even an older one)
// still routes proxied requests and fetches the new build's assets on demand. The
// matching new SW (registered by proxy.boot after the reload) is activated quietly
// in the background. We deliberately do NOT force a SW swap as part of the reload -
// that raced scramjet's controller config write and broke proxying.
function takeover() {
	reloadNow();
}

function onDetected(info) {
	targetId = info.buildId;
	latched = true;
	if (autoUpdateOn() && safeFn() && !alreadyTried(info.buildId)) takeover();
	else if (notifyOn()) showToast();
}

// Re-evaluate a deferred update when conditions may have become safe (e.g. the
// user switched away from the tab, or finished loading a page).
function maybeAuto() {
	if (!latched || refreshing || !targetId) return;
	if (autoUpdateOn() && safeFn() && !alreadyTried(targetId)) takeover();
}

async function check() {
	if (refreshing || !MY) return;
	const now = Date.now();
	if (latched) { maybeAuto(); return; } // already know - just re-check safety
	if (now - lastCheck < THROTTLE_MS) return;
	lastCheck = now;
	const info = await latest();
	if (!info || !info.buildId || info.buildId === MY) return;
	onDetected(info);
}

// --- toast ----------------------------------------------------------------
function showToast() {
	if (toastEl || !notifyOn()) return;
	toastEl = document.createElement("div");
	toastEl.className = "rv-update";
	toastEl.setAttribute("role", "status");

	const msg = document.createElement("span");
	msg.className = "rv-update-msg";
	msg.textContent = "A new version of Re:Vault is available.";

	const reload = document.createElement("button");
	reload.type = "button";
	reload.className = "rv-update-reload";
	reload.textContent = "Reload";
	reload.addEventListener("click", takeover);

	const dismiss = document.createElement("button");
	dismiss.type = "button";
	dismiss.className = "rv-update-x";
	dismiss.setAttribute("aria-label", "Dismiss");
	dismiss.textContent = "✕";
	dismiss.addEventListener("click", () => { if (toastEl) { toastEl.remove(); toastEl = null; } });

	toastEl.append(msg, reload, dismiss);
	document.body.appendChild(toastEl);
	emitIsland("update", { reload: takeover }); // Island Mode shows it in the island instead
	requestAnimationFrame(() => toastEl && toastEl.classList.add("rv-update-in"));
}

// --- public API -----------------------------------------------------------
// Called by errors.js / proxy.js when a first-party asset fails to load (often a
// pruned previous-generation file) - a strong signal we're on a stale build.
export function notifyAssetFailure() {
	lastCheck = 0; // bypass the throttle for this targeted check
	check();
}

export function initUpdates(opts = {}) {
	if (!MY || !window.fetch) return; // dev/unbuilt or unsupported
	if (typeof opts.isSafeToReload === "function") safeFn = opts.isSafeToReload;

	// Clear the loop-guard once we've actually landed on the build we reloaded for.
	try { if (sessionStorage.getItem(RELOAD_KEY) === MY) sessionStorage.removeItem(RELOAD_KEY); } catch (e) {}

	if (navigator.serviceWorker) {
		navigator.serviceWorker.addEventListener("controllerchange", () => {
			// Only reload when WE drove an update: a controller existed at load AND a
			// newer build was detected this session. This avoids reloading on a
			// first-ever SW install or on a manual unregister from settings.
			if (refreshing || !latched || !hadController) return;
			reloadNow();
		});
		navigator.serviceWorker.addEventListener("message", (e) => {
			// A SW from a newer build activated and claimed this tab (cross-tab). Mark
			// it so the guarded controllerchange (or next safety check) converges us.
			if (e.data && e.data.type === "RV_ACTIVATED" && e.data.buildId && e.data.buildId !== MY) {
				latched = true;
				targetId = e.data.buildId;
			}
		});
	}

	document.addEventListener("visibilitychange", () => {
		if (document.visibilityState === "visible") check();
		else maybeAuto(); // hidden tab is safe to reload in the background
	});
	window.addEventListener("focus", check);
	// Build id piggybacked on the presence beacon's reply: same decision as a poll result.
	window.addEventListener("rv:build", (e) => {
		const id = e && e.detail;
		if (refreshing || typeof id !== "string" || !id || id === MY) return;
		if (latched) { maybeAuto(); return; }
		onDetected({ buildId: id });
	});
	window.addEventListener("online", check);
	window.addEventListener("pageshow", (e) => { if (e.persisted) { lastCheck = 0; latched = false; check(); } });
	setInterval(check, POLL_MS);
	setTimeout(check, 30000); // first check once boot settles
}
